export const MAX_FRUIT_ROWS_PER_PAGE = 24;

// * Sizes in points on an A4 landscape page (842 x 595pt). The summary box sits below the
// * page padding (2 x 20), the report header (logo 112pt wide, up to 56pt high, + 5 margin)
// * and the page title (14pt Poppins + 10 margin); its own border, padding and margin take 32.
export const SUMMARY_HEIGHT_PER_PAGE = 430;
export const SUMMARY_LINE_HEIGHT = 13.5; // * 9pt Poppins
export const SUMMARY_ROW_GAP = 1; // * metaItems marginBottom
export const TABLE_ROW_HEIGHT = 19; // * tableCellHeight

const PAGE_CONTENT_WIDTH = 782; // * 842 minus 2 x 30 padding
const SUMMARY_BOX_CHROME_WIDTH = 22; // * border and padding of the summary box
const DEFECT_BOX_MARGIN = 10;
const CHAR_WIDTH = 5.5; // * 9pt Poppins, rounded up so estimates err towards more lines
const LABEL_SHARE = 0.5; // * label column maxWidth
const VALUE_SHARE = 0.7; // * value column maxWidth
// * display_vector truncates longer values to SUMMARY_VALUE_TRUNCATE_LENGTH characters + "..."
export const SUMMARY_VALUE_MAX_LENGTH = 152;
export const SUMMARY_VALUE_TRUNCATE_LENGTH = 150;

/**
 * Inner width of the summary box. It is 70% wide next to `defectBoxes` boxes of 40%, and
 * react-pdf shrinks all of them in proportion to their width when the row is too wide.
 */
export const summaryBoxInnerWidth = (defectBoxes: number): number => {
    const summary = 0.7 * PAGE_CONTENT_WIDTH;
    const defects = defectBoxes * 0.4 * PAGE_CONTENT_WIDTH;
    const overflow = summary + defects + defectBoxes * DEFECT_BOX_MARGIN - PAGE_CONTENT_WIDTH;
    const width = overflow > 0 ? summary - (overflow * summary) / (summary + defects) : summary;
    return width - SUMMARY_BOX_CHROME_WIDTH;
};

const truncateSummaryValue = (value: string) => (value.length > SUMMARY_VALUE_MAX_LENGTH
    ? `${value.substring(0, SUMMARY_VALUE_TRUNCATE_LENGTH)}...`
    : value);

// * react-pdf starts a new line at every "\n"
const longestLine = (text: string) => Math.max(...text.split("\n").map((line) => line.length));

export const estimateSummaryFieldLines = (label = "", value = "", innerWidth = summaryBoxInnerWidth(4)): number => {
    const labelText = `${label}:`;
    const valueText = truncateSummaryValue(value);
    // * Each column takes its text width up to its maxWidth. Together they may ask for more
    // * than the box (50% + 70%), and then react-pdf shrinks both in proportion.
    const labelWidth = Math.min(longestLine(labelText) * CHAR_WIDTH, innerWidth * LABEL_SHARE);
    const valueWidth = Math.min(longestLine(valueText) * CHAR_WIDTH, innerWidth * VALUE_SHARE);
    const scale = Math.min(1, innerWidth / (labelWidth + valueWidth));
    const lines = (text: string, width: number) => {
        const charsPerLine = Math.max(1, Math.floor((width * scale) / CHAR_WIDTH));
        return text.split("\n").reduce((total, line) => total + Math.max(1, Math.ceil(line.length / charsPerLine)), 0);
    };
    return Math.max(1, lines(labelText, labelWidth), lines(valueText, valueWidth));
};

const summaryFieldHeight = (lines: number) => lines * SUMMARY_LINE_HEIGHT + SUMMARY_ROW_GAP;

function chunkItems<T>(items: T[], size: number): T[][] {
    if (items.length === 0) return [[]];

    return Array.from(
        { length: Math.ceil(items.length / size) },
        (_, index) => items.slice(index * size, (index + 1) * size)
    );
}

function chunkFieldsByHeight<T>(fields: T[], heights: number[]): T[][] {
    const pages: T[][] = [[]];
    let pageHeight = 0;

    fields.forEach((field, index) => {
        const page = pages[pages.length - 1];
        if (page.length > 0 && pageHeight + heights[index] > SUMMARY_HEIGHT_PER_PAGE) {
            pages.push([field]);
            pageHeight = heights[index];
            return;
        }
        page.push(field);
        pageHeight += heights[index];
    });

    return pages;
}

export interface PDFCheckFruitTablePagePlan<Field, Row> {
    combineSummaryAndTable: boolean;
    summaryFieldPages: Field[][];
    tableRowPages: Row[][];
}

/**
 * Keep small reports compact, but explicitly paginate larger reports rather
 * than relying on react-pdf to split an overflowing flex layout.
 *
 * Pages are filled by estimated height rather than field count: labels wrap in
 * the narrow summary box, and a summary that overflows its page by a few points
 * makes react-pdf add a page holding only the bottom of the boxes.
 */
export const createPDFCheckFruitTablePagePlan = <Field, Row>(
    fields: Field[],
    rows: Row[],
    getFieldLines: (field: Field) => number = () => 1
): PDFCheckFruitTablePagePlan<Field, Row> => {
    const heights = fields.map((field) => summaryFieldHeight(getFieldLines(field)));
    const summaryFieldPages = chunkFieldsByHeight(fields, heights);
    const summaryHeight = heights.reduce((total, height) => total + height, 0);
    // * header and footer rows around the fruit rows
    const tableHeight = (rows.length + 2) * TABLE_ROW_HEIGHT;
    const combineSummaryAndTable = summaryFieldPages.length === 1
        && summaryHeight + tableHeight <= SUMMARY_HEIGHT_PER_PAGE;

    return {
        combineSummaryAndTable,
        summaryFieldPages,
        tableRowPages: combineSummaryAndTable
            ? []
            : chunkItems(rows, MAX_FRUIT_ROWS_PER_PAGE),
    };
};
